import React, { useEffect, useMemo, useState } from 'react';
import { Clock, AlertTriangle, RefreshCcw, Plus, Repeat, CalendarDays, Loader2, Trash2, Edit3, Zap, Eye } from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import {
  resolveEffectiveCapacite,
  isCapaciteOverride,
} from '../utils/clickAndCollectCapacite';

const JOUR_OPTIONS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 0, label: 'Dimanche' }
];

const defaultFormState = {
  type: 'RECURRENT',
  jourSemaine: 1,
  dateSpecifique: '',
  heureDebut: '09:00',
  heureFin: '10:00',
  capacite: 5,
  actif: true
};

const formatJour = (value) => {
  const option = JOUR_OPTIONS.find((item) => item.value === value);
  return option ? option.label : '-';
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.split('T')[0] || value;
  }
  return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const ClickAndCollectManager = ({ commercantId, onSettingsUpdated, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    clickAndCollect: false,
    dureePlageHoraireMinutes: 30,
    maxCommandesParPlage: 2,
    tempsPreparation: 15,
  });
  const [creneaux, setCreneaux] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [editingCreneau, setEditingCreneau] = useState(null);
  const [savingCreneau, setSavingCreneau] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const stats = useMemo(() => {
    const actifs = creneaux.filter((c) => c.actif).length;
    const recurrent = creneaux.filter((c) => c.type === 'RECURRENT').length;
    const exceptionnel = creneaux.filter((c) => c.type === 'EXCEPTIONNEL').length;
    return { actifs, recurrent, exceptionnel };
  }, [creneaux]);

  const previewList = useMemo(() => preview?.creneaux || [], [preview]);

  const isExistingPreview = (creneau) => {
    return creneaux.some(
      (existing) =>
        existing.jourSemaine === creneau.jourSemaine &&
        existing.heureDebut === creneau.heureDebut &&
        existing.heureFin === creneau.heureFin
    );
  };

  const closePreview = () => setPreview(null);

  const applyPreviewSync = async () => {
    await handleSyncFromHoraires({ skipConfirm: true });
  };

  useEffect(() => {
    if (!commercantId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getClickAndCollectSettings(commercantId);
      const commercantSettings = response?.commercant || {};
      
      // Synchroniser avec l'état du commerçant (depuis la vue d'ensemble)
      // Si clickAndCollect est activé dans la vue d'ensemble, on l'active aussi ici
      // Mais on ne force pas la désactivation si des créneaux spécifiques existent
      const creneauxExistants = response?.creneaux || [];
      const hasSpecificCreneaux = creneauxExistants.length > 0 && creneauxExistants.some(c => !c.autoGenerated);
      
      const prepRaw = commercantSettings.tempsPreparation;
      const prepNum = Number(prepRaw);
      setSettings({
        // Si le service est activé dans la vue d'ensemble OU si des créneaux spécifiques existent
        clickAndCollect: Boolean(commercantSettings.clickAndCollect) || hasSpecificCreneaux,
        dureePlageHoraireMinutes: commercantSettings.dureePlageHoraireMinutes ?? 30,
        maxCommandesParPlage: commercantSettings.maxCommandesParPlage ?? 2,
        // 15 = fallback défensif uniquement si la DB ne renvoie rien d’utilisable
        tempsPreparation:
          prepRaw !== undefined && prepRaw !== null && Number.isFinite(prepNum) && prepNum >= 0
            ? prepNum
            : 15,
      });
      setCreneaux(creneauxExistants);
    } catch (err) {
      console.error('Erreur chargement Click & Collect:', err);
      setError(err?.message || 'Impossible de charger la configuration Click & Collect.');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setFeedback('');
      const payload = {
        clickAndCollect: settings.clickAndCollect,
        dureePlageHoraireMinutes: Number(settings.dureePlageHoraireMinutes) || 30,
        maxCommandesParPlage: Number(settings.maxCommandesParPlage) || 2,
        tempsPreparation: Number(settings.tempsPreparation) || 15,
      };
      const response = await apiService.updateClickAndCollectSettings(commercantId, payload);
      const commercantData = response?.commercant || response;

      // Afficher immédiatement la valeur réellement enregistrée (évite un flash vers 15).
      if (commercantData && commercantData.tempsPreparation !== undefined && commercantData.tempsPreparation !== null) {
        const savedPrep = Number(commercantData.tempsPreparation);
        if (Number.isFinite(savedPrep) && savedPrep >= 0) {
          setSettings((prev) => ({
            ...prev,
            clickAndCollect: Boolean(commercantData.clickAndCollect ?? prev.clickAndCollect),
            dureePlageHoraireMinutes:
              commercantData.dureePlageHoraireMinutes ?? prev.dureePlageHoraireMinutes,
            maxCommandesParPlage: commercantData.maxCommandesParPlage ?? prev.maxCommandesParPlage,
            tempsPreparation: savedPrep,
          }));
        }
      }

      // Synchroniser avec la vue d'ensemble
      if (commercantData && onSettingsUpdated) {
        onSettingsUpdated({
          clickAndCollect: commercantData.clickAndCollect,
          dureePlageHoraireMinutes: commercantData.dureePlageHoraireMinutes,
          maxCommandesParPlage: commercantData.maxCommandesParPlage,
          tempsPreparation: commercantData.tempsPreparation,
        });
      } else if (onRefresh) {
        onRefresh();
      }
      setFeedback('Paramètres enregistrés avec succès.');
      // Recharger la liste pour afficher les capacités héritées (BL3-007B).
      await loadData();
    } catch (err) {
      console.error('Erreur sauvegarde settings:', err);
      setError(err?.message || 'Impossible de sauvegarder les paramètres.');
    } finally {
      setSavingSettings(false);
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  const handleSyncFromHoraires = async ({ skipConfirm = false } = {}) => {
    if (!skipConfirm) {
      const ok = window.confirm('Synchroniser automatiquement les créneaux à partir des horaires du commerçant ? Les créneaux automatisés existants seront remplacés.');
      if (!ok) return;
    }
    try {
      setSyncing(true);
      setFeedback('');
      await apiService.syncClickAndCollectAuto(commercantId);
      setFeedback('Créneaux synchronisés depuis les horaires.');
      setPreview(null);
      await loadData();
    } catch (err) {
      console.error('Erreur synchronisation auto:', err);
      setError(err?.message || 'Impossible de synchroniser depuis les horaires.');
    } finally {
      setSyncing(false);
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  const handlePreview = async () => {
    try {
      setLoadingPreview(true);
      setError('');
      const response = await apiService.previewClickAndCollectAuto(commercantId);
      setPreview(response);
    } catch (err) {
      console.error('Erreur prévisualisation:', err);
      setError(err?.message || 'Impossible de prévisualiser les créneaux.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const openCreateModal = () => {
    setEditingCreneau(null);
    setFormState({
      ...defaultFormState,
      capacite: Number(settings.maxCommandesParPlage) || 5,
    });
    setModalOpen(true);
  };

  const openEditModal = (creneau) => {
    setEditingCreneau(creneau);
    setFormState({
      type: creneau.type,
      jourSemaine: creneau.jourSemaine ?? 1,
      dateSpecifique: creneau.dateSpecifique ? new Date(creneau.dateSpecifique).toISOString().slice(0, 10) : '',
      heureDebut: creneau.heureDebut,
      heureFin: creneau.heureFin,
      capacite: creneau.capacite ?? creneau.placesTotal ?? settings.maxCommandesParPlage ?? 1,
      actif: creneau.actif ?? true
    });
    setModalOpen(true);
  };

  const handleFormChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitCreneau = async () => {
    try {
      setSavingCreneau(true);
      const payload = {
        type: formState.type,
        jourSemaine: formState.type === 'RECURRENT' ? Number(formState.jourSemaine) : undefined,
        dateSpecifique: formState.type === 'EXCEPTIONNEL' && formState.dateSpecifique ? formState.dateSpecifique : undefined,
        heureDebut: formState.heureDebut,
        heureFin: formState.heureFin,
        capacite: Number(formState.capacite) || 1,
        actif: formState.actif
      };

      if (editingCreneau) {
        await apiService.updateClickAndCollectCreneau(commercantId, editingCreneau.id, payload);
        setFeedback('Créneau mis à jour.');
      } else {
        await apiService.createClickAndCollectCreneau(commercantId, payload);
        setFeedback('Créneau ajouté.');
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      console.error('Erreur sauvegarde créneau:', err);
      setError(err?.message || 'Impossible de sauvegarder le créneau.');
    } finally {
      setSavingCreneau(false);
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  const handleDeleteCreneau = async (creneau) => {
    const confirmed = window.confirm(`Supprimer le créneau ${creneau.heureDebut}-${creneau.heureFin} ?`);
    if (!confirmed) return;
    try {
      await apiService.deleteClickAndCollectCreneau(commercantId, creneau.id);
      setFeedback('Créneau supprimé.');
      await loadData();
    } catch (err) {
      console.error('Erreur suppression créneau:', err);
      setError(err?.message || 'Impossible de supprimer le créneau.');
    } finally {
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {preview && (
        <Modal
          isOpen={!!preview}
          onClose={closePreview}
          title="Prévisualisation Click & Collect"
          maxWidth="max-w-3xl"
        >
          {loadingPreview ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : previewList.length === 0 ? (
            <div className="text-sm text-gray-600">
              Aucun créneau ne serait généré. Vérifie que tes horaires sont actifs.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Ces créneaux seront créés automatiquement à partir des horaires configurés. Les entrées marquées
                « existantes » correspondent déjà à un créneau actif.
              </p>
              <div className="overflow-x-auto border border-gray-100 rounded-2xl">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Jour</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Horaires</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Capacité</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {previewList.map((item, index) => {
                      const exists = isExistingPreview(item);
                      return (
                        <tr key={`${item.jourSemaine}-${item.heureDebut}-${item.heureFin}-${index}`}>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">{formatJour(item.jourSemaine)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.heureDebut} - {item.heureFin}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.capacite ?? settings?.maxCommandesParPlage ?? 1} commandes / créneau
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                exists ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
                              }`}
                            >
                              {exists ? 'Existe déjà' : 'Nouveau'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={closePreview}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Fermer
                </button>
                <button
                  onClick={applyPreviewSync}
                  disabled={syncing}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Appliquer ces créneaux
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {feedback && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {feedback}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Configuration Click & Collect</h3>
            <p className="text-sm text-gray-500">
              Activez le service et définissez les capacités globales de retrait.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={loadData}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Rafraîchir
            </button>
            <button
              onClick={handlePreview}
              disabled={loadingPreview}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
            >
              {loadingPreview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Prévisualiser
            </button>
            <button
              onClick={handleSyncFromHoraires}
              disabled={syncing}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
            >
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Synchroniser depuis horaires
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sauvegarder
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 border rounded-xl">
            <div className="text-sm text-gray-500 mb-2">Service Click & Collect</div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleSettingChange('clickAndCollect', !settings.clickAndCollect)}
                className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${
                  settings.clickAndCollect ? 'bg-green-500' : 'bg-gray-300'
                }`}
                type="button"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                    settings.clickAndCollect ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-base font-semibold text-gray-900">
                {settings.clickAndCollect ? 'Activé' : 'Désactivé'}
              </span>
            </div>
            {!settings.clickAndCollect && (
              <div className="mt-3 text-xs text-gray-500 bg-blue-50 p-2 rounded border border-blue-200">
                <p className="font-medium text-blue-700 mb-1">ℹ️ Mode automatique (par défaut)</p>
                <p>Si aucun créneau spécifique n'est configuré, les créneaux sont générés automatiquement depuis vos horaires d'ouverture.</p>
                <p className="mt-1 font-medium text-blue-700">Activez ce toggle uniquement si vous souhaitez créer des créneaux spécifiques différents de vos horaires d'ouverture.</p>
              </div>
            )}
          </div>

          <div className="p-4 border rounded-xl">
            <div className="text-sm text-gray-500 mb-2">Temps de préparation par défaut</div>
            <input
              type="number"
              min="0"
              max="480"
              step="5"
              value={
                settings.tempsPreparation === '' || settings.tempsPreparation === null
                  ? ''
                  : settings.tempsPreparation
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  handleSettingChange('tempsPreparation', '');
                  return;
                }
                const n = parseInt(raw, 10);
                if (!Number.isFinite(n)) return;
                handleSettingChange('tempsPreparation', n);
              }}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            <p className="text-xs text-gray-500 mt-2">
              Ce délai est utilisé par défaut pour vos commandes Click &amp; Collect et Livraison.
              Vous pouvez définir un délai différent sur certains produits.
            </p>
          </div>

          <div className="p-4 border rounded-xl">
            <div className="text-sm text-gray-500 mb-2">Durée d’un créneau (minutes)</div>
            <input
              type="number"
              min="5"
              max="480"
              step="5"
              value={settings.dureePlageHoraireMinutes}
              onChange={(e) => handleSettingChange('dureePlageHoraireMinutes', e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="p-4 border rounded-xl">
            <div className="text-sm text-gray-500 mb-2">Commandes max / créneau</div>
            <input
              type="number"
              min="1"
              max="500"
              value={settings.maxCommandesParPlage}
              onChange={(e) => handleSettingChange('maxCommandesParPlage', e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-xl flex items-center gap-3">
            <Clock className="h-10 w-10 text-primary" />
            <div>
              <div className="text-2xl font-semibold text-gray-900">{stats.actifs}</div>
              <div className="text-sm text-gray-500">Créneaux actifs</div>
            </div>
          </div>
          <div className="p-4 border rounded-xl flex items-center gap-3">
            <Repeat className="h-10 w-10 text-indigo-500" />
            <div>
              <div className="text-2xl font-semibold text-gray-900">{stats.recurrent}</div>
              <div className="text-sm text-gray-500">Récurrents</div>
            </div>
          </div>
          <div className="p-4 border rounded-xl flex items-center gap-3">
            <CalendarDays className="h-10 w-10 text-teal-500" />
            <div>
              <div className="text-2xl font-semibold text-gray-900">{stats.exceptionnel}</div>
              <div className="text-sm text-gray-500">Exceptionnels</div>
            </div>
          </div>

        </div>

        {!settings.clickAndCollect && creneaux.length === 0 && (
          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium mb-1">Mode automatique (par défaut)</p>
                <p>Les créneaux sont générés automatiquement depuis vos horaires d'ouverture. Activez ce toggle uniquement si vous souhaitez créer des créneaux spécifiques différents de vos horaires.</p>
              </div>
            </div>
          </div>
        )}
        {settings.clickAndCollect && creneaux.length === 0 && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <div>
              <p className="font-medium mb-1">Aucun créneau spécifique configuré</p>
              <p>Les créneaux sont générés automatiquement depuis vos horaires d'ouverture. Ajoutez des créneaux spécifiques ci-dessous si vous souhaitez des horaires différents.</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Créneaux disponibles</h3>
            <p className="text-sm text-gray-500">
              Planifiez les créneaux de retrait spécifiques. Les créneaux récurrents s'appliquent chaque semaine.
              <br />
              <span className="text-xs text-gray-400">
                💡 Si aucun créneau spécifique n'est configuré, les créneaux sont générés automatiquement depuis vos horaires d'ouverture.
              </span>
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm transition-colors whitespace-nowrap flex-shrink-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un créneau
          </button>
        </div>

        {creneaux.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
            <p className="text-gray-700 font-medium">Aucun créneau spécifique configuré</p>
            <p className="text-sm text-gray-500 mt-2 mb-4">
              Les créneaux sont générés automatiquement depuis vos horaires d'ouverture.
              <br />
              Ajoutez un créneau spécifique ci-dessous si vous souhaitez des horaires différents.
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un premier créneau spécifique
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Jour / Date</th>
                    <th className="px-4 py-3">Horaires</th>
                    <th className="px-4 py-3">
                      Capacité <span className="text-xs text-gray-400">(commandes / créneau)</span>
                    </th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
              <tbody className="divide-y divide-gray-100">
                {creneaux.map((creneau) => (
                  <tr key={creneau.id || `${creneau.type}-${creneau.horaire}-${creneau.dateSpecifique || creneau.jourSemaine}`}>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          creneau.type === 'RECURRENT'
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'bg-teal-50 text-teal-700'
                        }`}
                      >
                        {creneau.type === 'RECURRENT' ? 'Récurrent' : 'Exceptionnel'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {creneau.type === 'RECURRENT'
                        ? formatJour(creneau.jourSemaine)
                        : formatDate(creneau.dateSpecifique)}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {creneau.heureDebut} - {creneau.heureFin}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 font-medium">
                        {resolveEffectiveCapacite(creneau, settings.maxCommandesParPlage)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {isCapaciteOverride(creneau, settings.maxCommandesParPlage)
                          ? 'Personnalisé'
                          : 'Hérite de la capacité globale'}
                        {creneau.placesRestantes !== undefined && (
                          <> · Restant : {Math.max(0, creneau.placesRestantes)}</>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          creneau.disponible || creneau.actif
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {creneau.disponible || creneau.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(creneau)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
                        >
                          <Edit3 className="h-3.5 w-3.5 mr-1" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteCreneau(creneau)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCreneau ? 'Modifier le créneau' : 'Ajouter un créneau'}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de créneau</label>
              <select
                value={formState.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="RECURRENT">Récurrent (hebdomadaire)</option>
                <option value="EXCEPTIONNEL">Exceptionnel (date unique)</option>
              </select>
            </div>

            {formState.type === 'RECURRENT' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jour de la semaine</label>
                <select
                  value={formState.jourSemaine}
                  onChange={(e) => handleFormChange('jourSemaine', Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  {JOUR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formState.type === 'EXCEPTIONNEL' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date spécifique</label>
                <input
                  type="date"
                  value={formState.dateSpecifique}
                  onChange={(e) => handleFormChange('dateSpecifique', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Heure de début</label>
              <input
                type="time"
                value={formState.heureDebut}
                onChange={(e) => handleFormChange('heureDebut', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Heure de fin</label>
              <input
                type="time"
                value={formState.heureFin}
                onChange={(e) => handleFormChange('heureFin', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capacité (commandes / créneau)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Nombre de commandes simultanées par créneau (ex&nbsp;: 4 commandes toutes les {settings?.dureePlageHoraireMinutes || 30} min)
              </p>
              <input
                type="number"
                min="1"
                max="500"
                value={formState.capacite}
                onChange={(e) => handleFormChange('capacite', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleFormChange('actif', !formState.actif)}
                className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${
                  formState.actif ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                    formState.actif ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">
                {formState.actif ? 'Créneau actif' : 'Créneau désactivé'}
              </span>
            </div>
          </div>

          <div className="sticky bottom-0 -mx-6 -mb-6 mt-4 flex items-center justify-between gap-3 border-t border-gray-100 bg-white px-6 py-4">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            {/*
              Lot H / BL3-007C : bg-primary / primary-dark n'existent pas dans le thème
              Tailwind (seulement primary-50…900). Le bouton était rendu en texte blanc
              sans fond → invisible sur le terrain. Utiliser primary-600/700.
            */}
            <button
              type="button"
              disabled={savingCreneau}
              onClick={handleSubmitCreneau}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {savingCreneau && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCreneau ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClickAndCollectManager;

